/* 请求方式的再封装，如果不希望对接口错误做定制处理，采用此种方式把错误toast即可 */
import { domain } from '@finance/domain'
import ajax from '@finance/ajax'
import axios, { AxiosRequestConfig } from 'axios'
import Cookies from 'js-cookie'
import { random, padStart } from 'lodash'
import md5 from 'crypto-js/md5'
import Toast from '@common/toast'
import useragent from '@finance/useragent'
import store from '@/stores/index'
import { getPhoneInfo } from '@/utils/client'

const Loading = {
  count: 0,
  show() {
    this.count++
    if (this.count > 0) {
      store.commit('showLoading')
    }
  },
  hide() {
    this.count--
    if (this.count === 0) {
      store.commit('hideLoading')
    }
  }
}

type RequestMethod =
  | 'get' | 'GET'
  | 'post' | 'POST'
  | 'put' | 'PUT'

interface RequestConfig {
  service?: string
  method?: RequestMethod
  headers?: any
  loading?: boolean
  retry?: boolean
  retryNum?: number
  showError?: boolean
  errorMsg?: string
  customDomain?: string
}

// 随云http请求
const wsService = axios.create({
  baseURL: domain.yunapi,
  timeout: 10000
})
// 请求拦截
wsService.interceptors.request.use(
  async(config) => {
    const clientKey = 'PiVEoJM9OHFS8xFlnD3CuSrJgRgyVLwS'
    const clientSecret = 'pQhGxs0I84zQgeU8'
    const nonceStr = padStart((random(0, 999999999999999) + ''), 16, '0')
    const timestamp = new Date().getTime()
    const sign = md5(clientKey + nonceStr + timestamp + clientSecret).toString()
    const token = Cookies.get('ssj_token')
    // const { bookId } = store.state.books.currentBook // 统一账本id提交
    let { phoneInfo } = store.getters
    if (!phoneInfo.UUID && (useragent.isSuishouji || useragent.isKaniu)) {
      phoneInfo = await getPhoneInfo()
    }
    config.headers = {
      Device: JSON.stringify({
        model: phoneInfo.deviceModel,
        platform: phoneInfo.platform,
        os_version: phoneInfo.osVersion,
        device_id: phoneInfo.UUID,
        product_name: phoneInfo.productName,
        product_version: phoneInfo.appVersion,
        locale: phoneInfo.language,
        timeZone: phoneInfo.timezone
      }),
      'Minor-Version': '0.0.0',
      'Client-Key': clientKey,
      'Nonce-Str': nonceStr,
      Timestamp: timestamp,
      Sign: sign,
      ...config.headers
    }
    if (config.method && config.method.toLocaleLowerCase() === 'post') {
      config.headers['Content-Type'] = 'application/json'
    }
    if (token) {
      config.headers.Authorization = token ? 'Bearer ' + token : ''
    }
    // if (bookId) {
    //   config.headers['Trading-Entity'] = bookId
    // }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

/**
 * http 请求函数
 * @param url url地址
 * @param body 请求体
 * @param config.service 服务类型 finance: 理财 ws：随云
 * @param config.method 请求方法
 * @param config.loading 是否显示请求loading
 * @param config.retry 是否开启失败重试
 * @param config.retryNum 失败重试次数
 * @param config.showError 是否显示失败信息
 */
const request = (
  url: string,
  data: object = {},
  {
    service = 'finance',
    method = 'post',
    loading = true,
    retry = false,
    retryNum = 1,
    showError = true,
    errorMsg = '',
    customDomain = ''
  }: RequestConfig = {}
): Promise<any> => {
  loading && Loading.show()

  if (customDomain) {
    const options = {
      url: customDomain + url,
      method,
      timeout: 10000,
      headers: {
        Authorization: 'Bearer ' + (Cookies.get('ssj_token') || '')
      },
      data,
      withCredentials: true
    }

    return axios(options).then((result: any) => {
      loading && Loading.hide()
      const { code } = result.data || {}
      if (code !== '0000' && code !== '3001') {
        return Promise.reject(result)
      }
      return result.data
    }).catch((error: any) => {
      console.error(url, error)
      loading && Loading.hide()

      const msg = errorMsg || error.message || error.msg || '服务异常，数据上报失败！'
      const { code } = error.data || {}
      if (!code || (code && code === '7001')) {
        showError && Toast(msg)
        // return Promise.reject(error)
      }
    })
  } else if (service === 'finance') {
    return ajax.request({
      url: domain.api + url,
      body: data,
      method,
      headers: {
        Authorization: 'Bearer ' + (Cookies.get('ssj_token') || '')
      }
    }).then((result) => {
      loading && Loading.hide()

      if (!result.succeed) {
        return Promise.reject(result)
      }
      return result.data
    }).catch((error) => {
      console.error(url, error)
      loading && Loading.hide()

      if (retry && retryNum < 3) {
        return request(url, data, { service, method, loading, retry, retryNum: retryNum + 1 })
      } else {
        const msg = errorMsg || error.message || error.msg || '当前活动太火爆了,请稍等几分钟后再次进入活动页重试'
        showError && Toast(msg)
      }
      return Promise.reject(error)
    })
  } else {
    const requestConfig: AxiosRequestConfig = {
      url,
      method
    }
    if (method.toLocaleLowerCase() === 'get') {
      requestConfig.params = data
    } else {
      requestConfig.data = data
    }
    return wsService(requestConfig).then((result) => {
      loading && Loading.hide()

      return result.data
    }).catch((error) => {
      console.error(url, error)
      loading && Loading.hide()

      if (retry && retryNum < 3) {
        return request(url, data, { loading, retry, retryNum: retryNum + 1 })
      } else {
        let msg = ''
        const response = error.response || {}
        if (response.data && response.data.message) {
          msg = response.data.message
        } else if (error.message.indexOf('timeout') !== -1) {
          msg = '数据请求超时，请稍后再试'
        } else if (error.message.indexOf('Network Error') !== -1) {
          msg = '世界上最遥远的距离，就是没有网络'
        } else {
          msg = errorMsg || error.message || error.msg || '当前活动太火爆了,请稍等几分钟后再次进入活动页重试'
        }
        showError && Toast(msg)
      }
      return Promise.reject(error)
    })
  }
}

/**
 * 随云get请求
 * @param url url地址
 * @param body 请求体
 * @param config 请求配置
 */
export const post = (url = '', body = {}, config: RequestConfig = {}) => {
  const mergeOption: RequestConfig = {
    ...config,
    service: 'ws',
    method: 'post'
  }
  return request(url, body, mergeOption)
}

/**
 * 随云post请求
 * @param url url地址
 * @param params params参数
 * @param config 请求配置
 */
export const get = (url = '', params = {}, config: RequestConfig = {}) => {
  const mergeOption: RequestConfig = {
    ...config,
    service: 'ws',
    method: 'get'
  }
  return request(url, params, mergeOption)
}

export default request
